import { useRef, useEffect, useState, useMemo, createContext, useContext, CSSProperties, Fragment } from 'react';

// ─── Color tokens ─────────────────────────────────────────────────────────────
const C = {
  navy950:      'rgb(8 12 22)',
  navy900:      'rgb(15 23 41)',
  teal700:      'rgb(15 118 110)',
  teal600:      'rgb(13 148 136)',
  teal500:      'rgb(20 184 166)',
  teal400:      'rgb(45 212 191)',
  teal300:      'rgb(94 234 212)',
  teal100:      'rgb(204 251 241)',
  cream100:     '#fef3c7',
  sand400:      '#d4a574',
  amber300:     '#fcd34d',
  rose:         '#f87171',
  border:       'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.16)',
  textMuted:    'rgba(204,251,241,0.55)',
  textDim:      'rgba(204,251,241,0.35)',
  textBody:     'rgba(204,251,241,0.78)',
  textHigh:     'rgba(254,243,199,0.95)',
} as const;

const FONT = {
  display: 'Plus Jakarta Sans, -apple-system, sans-serif',
  sans:    'Outfit, -apple-system, sans-serif',
  mono:    'JetBrains Mono, ui-monospace, monospace',
} as const;

// ─── Stage constants ──────────────────────────────────────────────────────────
const STAGE_W = 1920;
const STAGE_H = 1080;

// Layout regions
const CAP_X = 140, CAP_Y = 200;
const SAMPLE_X = 140, SAMPLE_Y = 460;
const MINI_Y = SAMPLE_Y + 160; // 620
const BAR_X = 140, BAR_Y = 855, BAR_W = 1640;

// Library card grid
const LIBRARY_DATA = [
  { glyph: 'lung',     tissue: 'Lung',        disease: 'NSCLC · Tumor',  version: 'v3.1' },
  { glyph: 'blood',    tissue: 'PBMC',        disease: 'Healthy',        version: 'v2.4' },
  { glyph: 'brain',    tissue: 'Brain',       disease: 'Glioblastoma',   version: 'v1.8' },
  { glyph: 'liver',    tissue: 'Liver',       disease: 'HCC · Tumor',    version: 'v2.0' },
  { glyph: 'skin',     tissue: 'Skin',        disease: 'Melanoma',       version: 'v2.2' },
  { glyph: 'breast',   tissue: 'Breast',      disease: 'TNBC',           version: 'v1.6' },
  { glyph: 'kidney',   tissue: 'Kidney',      disease: 'ccRCC',          version: 'v1.4' },
  { glyph: 'colon',    tissue: 'Colon',       disease: 'CRC · Tumor',    version: 'v2.1' },
  { glyph: 'pancreas', tissue: 'Pancreas',    disease: 'PDAC',           version: 'v1.2' },
  { glyph: 'blood',    tissue: 'Bone marrow', disease: 'AML',            version: 'v1.9' },
  { glyph: 'thymus',   tissue: 'Thymus',      disease: 'Healthy',        version: 'v1.0' },
  { glyph: 'heart',    tissue: 'Heart',       disease: 'Cardiomyopathy', version: 'v1.1' },
];
const LIB_COLS = 4;
const CARD_W = 210, CARD_H = 100, CARD_GAP = 14;
const LIB_W = LIB_COLS * CARD_W + (LIB_COLS - 1) * CARD_GAP;
const LIB_X = STAGE_W - LIB_W - 140;
const LIB_Y = 320;

function libCardPos(idx: number) {
  const col = idx % LIB_COLS;
  const row = Math.floor(idx / LIB_COLS);
  return { x: LIB_X + col * (CARD_W + CARD_GAP), y: LIB_Y + row * (CARD_H + CARD_GAP) };
}

// Scene D act-2 multi-row layout
const ROW_Y     = [320, 420, 520, 620];
const CHIP_W    = 300;
const CHIP_X    = 140;
const CARD_X    = 560;
const CARD_MID_OFFSET_Y = -15;
const ROW_MID_Y = (i: number) => ROW_Y[i] + 38;

const ACT2_MATCHES = [
  { row: 0, libIdx: 0, label: 'SAMPLE_042', sublabel: 'Lung tumor biopsy' },
  { row: 1, libIdx: 2, label: 'SAMPLE_117', sublabel: 'Brain biopsy' },
  { row: 2, libIdx: 3, label: 'SAMPLE_088', sublabel: 'Liver tumor' },
  { row: 3, libIdx: 1, label: 'SAMPLE_023', sublabel: 'Whole blood' },
];

// Cell-type data
const CELL_TYPES = [
  { key: 'tcd8',  label: 'CD8 T cells',  color: C.teal700 },
  { key: 'tcd4',  label: 'CD4 T cells',  color: C.teal600 },
  { key: 'tregs', label: 'Tregs',         color: C.teal500 },
  { key: 'b',     label: 'B cells',       color: C.teal400 },
  { key: 'nk',    label: 'NK cells',      color: C.teal300 },
  { key: 'mono',  label: 'Monocytes',     color: C.cream100 },
  { key: 'macs',  label: 'Macrophages',   color: C.sand400 },
  { key: 'dc',    label: 'Dendritic',     color: C.amber300 },
];

const RIGHT_PROPORTIONS = [0.22, 0.18, 0.06, 0.10, 0.08, 0.12, 0.18, 0.06];
const WRONG_PROPORTIONS  = [0.38, 0.34, 0.02, 0.18, 0.04, 0.04, 0.00, 0.00];

// Histogram bar labels — two-line format
const HIST_LABELS: [string, string][] = [
  ['CD8+',     'T cells'],
  ['CD4+',     'T cells'],
  ['Tregs',    ''],
  ['B',        'cells'],
  ['NK',       'cells'],
  ['Mono-',    'cytes'],
  ['Macro-',   'phages'],
  ['Dendritic',''],
];

// 5 rings that map to the ArcMark logo geometry (inner dark teal → outer cream).
// Each ring corresponds to one concentric arc in the brand mark; colors match
// the ArcMark gradient endpoints so the morph reads as the data becoming the logo.
// Gradient pairs match ArcMark exactly (dark teal inner → cream outer, diagonal).
const LOGO_RINGS = [
  { label: 'CD8+ T cells', color: '#115e59', gradFrom: '#052e16', gradTo: '#115e59' },
  { label: 'CD4+ T cells', color: '#0f766e', gradFrom: '#115e59', gradTo: '#0f766e' },
  { label: 'NK + B cells', color: '#14b8a6', gradFrom: '#0f766e', gradTo: '#14b8a6' },
  { label: 'Monocytes',    color: C.sand400, gradFrom: '#14b8a6', gradTo: '#d4a574' },
  { label: 'Macrophages',  color: C.cream100,gradFrom: '#d4a574', gradTo: '#fef3c7' },
];
// Proportions give the histogram visual variety; arc spans are all 270° (matching
// the logo's uniform arcs) so the morph resolves to the exact brand mark shape.
const LOGO_PROPORTIONS = [0.28, 0.22, 0.20, 0.18, 0.12];
const LOGO_LABELS: [string, string][] = [
  ['CD8+',  'T cells'],
  ['CD4+',  'T cells'],
  ['NK+B',  'cells'],
  ['Mono-', 'cytes'],
  ['Macro-','phages'],
];

// ─── Easing helpers ───────────────────────────────────────────────────────────
const ss    = (t: number) => { const c = Math.max(0, Math.min(1, t)); return c * c * (3 - 2 * c); };
const lerp  = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// ─── Animation engine ─────────────────────────────────────────────────────────
interface TLCtx { time: number; duration: number; }
const TimelineContext = createContext<TLCtx>({ time: 0, duration: 27 });
const useTime     = () => useContext(TimelineContext).time;
const useTimeline = () => useContext(TimelineContext);

interface SPCtx { localTime: number; progress: number; duration: number; }
const SpriteContext = createContext<SPCtx>({ localTime: 0, progress: 0, duration: 0 });
const useSprite = () => useContext(SpriteContext);

function Sprite({ start, end, children }: { start: number; end: number; children: React.ReactNode }) {
  const { time } = useTimeline();
  if (time < start || time > end) return null;
  const dur = end - start;
  const lt  = Math.max(0, time - start);
  return (
    <SpriteContext.Provider value={{ localTime: lt, progress: dur > 0 ? clamp(lt / dur, 0, 1) : 0, duration: dur }}>
      {children}
    </SpriteContext.Provider>
  );
}

// ─── Visual primitives ────────────────────────────────────────────────────────

function Halo({ x, y, size = 600, color = 'rgba(20,184,166,0.18)', opacity = 1 }: {
  x: number; y: number; size?: number; color?: string; opacity?: number;
}) {
  return (
    <div style={{
      position: 'absolute',
      left: x - size / 2, top: y - size / 2,
      width: size, height: size,
      borderRadius: '50%',
      background: color,
      filter: 'blur(80px)',
      opacity,
      pointerEvents: 'none',
    }} />
  );
}

function GridBackground({ opacity = 0.05, tile = 72 }: { opacity?: number; tile?: number }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: `linear-gradient(to right, rgba(20,184,166,${opacity}) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(20,184,166,${opacity}) 1px, transparent 1px)`,
      backgroundSize: `${tile}px ${tile}px`,
      pointerEvents: 'none',
    }} />
  );
}

function intensityToTeal(v: number): string {
  if (v < 0.08) return 'rgba(15,118,110,0.08)';
  const stops: [number, [number, number, number, number]][] = [
    [0.00, [2,  24,  22, 0.60]],
    [0.25, [15, 118, 110, 0.75]],
    [0.55, [20, 184, 166, 0.90]],
    [0.85, [94, 234, 212, 0.95]],
    [1.00, [212, 165, 116, 1.00]],
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, c0] = stops[i], [t1, c1] = stops[i + 1];
    if (v >= t0 && v <= t1) {
      const k = (v - t0) / (t1 - t0);
      return `rgba(${(c0[0] + (c1[0] - c0[0]) * k) | 0},${(c0[1] + (c1[1] - c0[1]) * k) | 0},${(c0[2] + (c1[2] - c0[2]) * k) | 0},${(c0[3] + (c1[3] - c0[3]) * k).toFixed(2)})`;
    }
  }
  return 'rgba(20,184,166,0.9)';
}

function seeded(seed: number) {
  let s = seed;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
}

function MatrixGrid({ x, y, cols = 26, rows = 64, cell = 11, gap = 1, revealProgress = 1, scanLine = false,
  scanProgress = 0, noiseSeed = 7, pulse = false, pulsePhase = 0 }: {
  x: number; y: number; cols?: number; rows?: number; cell?: number; gap?: number;
  revealProgress?: number; scanLine?: boolean; scanProgress?: number;
  noiseSeed?: number; pulse?: boolean; pulsePhase?: number;
}) {
  const grid = useMemo(() => {
    const rand = seeded(noiseSeed);
    const out: number[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[] = [];
      for (let c = 0; c < cols; c++) {
        const wave1 = Math.sin(c / cols * 6 + r * 0.5) * 0.5 + 0.5;
        const wave2 = Math.cos(r / rows * 4 + c * 0.2) * 0.4 + 0.4;
        const noise = rand();
        const v = Math.max(0, Math.min(1, 0.18 * wave1 + 0.28 * wave2 + 0.55 * noise - 0.1));
        const dropout = rand() < 0.22 ? 0 : 1;
        row.push(v * dropout);
      }
      out.push(row);
    }
    return out;
  }, [cols, rows, noiseSeed]);

  const w = cols * cell + (cols - 1) * gap;
  const h = rows * cell + (rows - 1) * gap;

  return (
    <div style={{ position: 'absolute', left: x, top: y, width: w, height: h }}>
      {grid.map((row, r) =>
        row.map((v, c) => {
          const colFrac = c / cols;
          const local = (revealProgress - colFrac) * 4;
          const reveal = Math.max(0, Math.min(1, local));
          if (reveal <= 0) return null;
          let glow = 0;
          if (scanLine) { const dist = Math.abs(colFrac - scanProgress); glow = Math.max(0, 1 - dist * 9); }
          let pulseMod = 1;
          if (pulse && v > 0.3) { pulseMod = 0.85 + 0.15 * Math.sin(pulsePhase + r * 0.3 + c * 0.2); }
          return (
            <div key={`${r}-${c}`} style={{
              position: 'absolute',
              left: c * (cell + gap), top: r * (cell + gap),
              width: cell, height: cell,
              background: intensityToTeal(v),
              opacity: reveal * pulseMod,
              boxShadow: glow > 0.3
                ? `0 0 ${glow * 12}px ${glow * 6}px rgba(254,243,199,${glow * 0.45})`
                : 'none',
              borderRadius: 1,
            }} />
          );
        })
      )}
    </div>
  );
}

function SampleChip({ x, y, label = 'SAMPLE_042', sublabel = 'Lung tumor biopsy', tone = 'default', scale = 1, width = 300 }: {
  x: number; y: number; label?: string; sublabel?: string; tone?: string; scale?: number; width?: number;
}) {
  const accent = tone === 'wrong' ? C.rose : tone === 'right' ? C.teal400 : C.teal300;
  return (
    <div style={{
      position: 'absolute', left: x, top: y,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      width,
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 20px',
      background: 'rgba(15,23,41,0.85)',
      border: `1px solid ${C.borderStrong}`,
      borderRadius: 12,
      fontFamily: FONT.sans,
      boxSizing: 'border-box',
      transition: 'border-color 320ms cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'rgba(20,184,166,0.12)',
        border: `1px solid ${accent}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: 'border-color 320ms cubic-bezier(0.16,1,0.3,1)',
      }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="6" stroke={accent} strokeWidth="1.6" />
          <circle cx="10" cy="10" r="2" fill={accent} />
        </svg>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 12, color: C.textMuted, letterSpacing: '0.18em', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ fontFamily: FONT.sans, fontSize: 16, color: C.textHigh, fontWeight: 500, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sublabel}</div>
      </div>
    </div>
  );
}

function TissueGlyph({ kind, color = 'currentColor', size = 28 }: { kind: string; color?: string; size?: number }) {
  const stroke = { stroke: color, strokeWidth: 1.6, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (kind === 'lung')     return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M14 5v8M9 8c-3 1-4 5-3.5 9 .3 2 1.5 3 3.5 3s3-1 3-3.5V11c0-2-1.5-3.5-3-3z" {...stroke}/><path d="M19 8c3 1 4 5 3.5 9-.3 2-1.5 3-3.5 3s-3-1-3-3.5V11c0-2 1.5-3.5 3-3z" {...stroke}/></svg>;
  if (kind === 'brain')    return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M14 6c-3 0-5 2-5 5 0 1-1 1.5-1 3 0 2 1.5 3.5 3 3.5h3V6zM14 6c3 0 5 2 5 5 0 1 1 1.5 1 3 0 2-1.5 3.5-3 3.5h-3V6z" {...stroke}/></svg>;
  if (kind === 'blood')    return <svg width={size} height={size} viewBox="0 0 28 28"><circle cx="10" cy="11" r="3" {...stroke}/><circle cx="18" cy="14" r="3" {...stroke}/><circle cx="13" cy="19" r="3" {...stroke}/></svg>;
  if (kind === 'liver')    return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M5 10c2-3 8-4 13-3 3 .5 5 3 5 6 0 4-4 7-9 7-6 0-10-3-10-7 0-1 .3-2 1-3z" {...stroke}/><path d="M14 10v8" {...stroke}/></svg>;
  if (kind === 'kidney')   return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M14 5c4 0 7 3 7 7v3c0 4-3 7-7 7s-7-3-7-7v-3c0-4 3-7 7-7z" {...stroke}/><circle cx="14" cy="14" r="2" {...stroke}/></svg>;
  if (kind === 'skin')     return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M4 9h20M4 14h20M4 19h20" {...stroke}/><circle cx="18" cy="14" r="2" {...stroke}/></svg>;
  if (kind === 'breast')   return <svg width={size} height={size} viewBox="0 0 28 28"><circle cx="14" cy="14" r="8" {...stroke}/><circle cx="14" cy="14" r="2" {...stroke}/></svg>;
  if (kind === 'colon')    return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M5 8c4 0 4 5 0 5s-4 5 0 5h18M22 5v3M22 18v3" {...stroke}/></svg>;
  if (kind === 'pancreas') return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M5 14c0-4 3-7 7-7s8 3 11 7c-2 4-7 7-11 7s-7-3-7-7z" {...stroke}/></svg>;
  if (kind === 'heart')    return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M14 22S5 16 5 11a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 5-9 11-9 11z" {...stroke}/></svg>;
  if (kind === 'thymus')   return <svg width={size} height={size} viewBox="0 0 28 28"><path d="M9 6v7c0 4 5 9 5 9s5-5 5-9V6" {...stroke}/><path d="M9 9h10" {...stroke}/></svg>;
  return null;
}

function SignatureCard({ glyph, tissue, disease, version = 'v2.4', state = 'idle', width = 200, height = 100 }: {
  glyph: string; tissue: string; disease: string; version?: string;
  state?: 'idle' | 'dim' | 'highlight' | 'wrong' | 'right' | 'matched'; width?: number; height?: number;
}) {
  let bg = 'rgba(15,23,41,0.6)', border: string = C.border, glow = 'none', accent: string = C.teal300, opacity = 1;
  if (state === 'dim')     { opacity = 0.22; }
  else if (state === 'highlight') { border = 'rgba(212,165,116,0.55)'; bg = 'rgba(212,165,116,0.05)'; accent = C.sand400; }
  else if (state === 'wrong')     { border = 'rgba(248,113,113,0.65)'; bg = 'rgba(248,113,113,0.08)'; glow = '0 0 24px -4px rgba(248,113,113,0.55)'; accent = C.rose; }
  else if (state === 'right' || state === 'matched') { border = 'rgba(94,234,212,0.7)'; bg = 'rgba(20,184,166,0.10)'; glow = '0 0 36px -6px rgba(45,212,191,0.5)'; accent = C.teal300; }

  const isMatchedOrRight = state === 'matched' || state === 'right';

  return (
    <div style={{ width, height, background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: 14,
      display: 'flex', flexDirection: 'column', gap: 6, fontFamily: FONT.sans,
      boxShadow: glow, opacity, boxSizing: 'border-box',
      transition: 'opacity 280ms cubic-bezier(0.16,1,0.3,1), box-shadow 280ms, border-color 280ms, background 280ms',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 22 }}>
        <div style={{ color: accent, opacity: 0.95 }}>
          <TissueGlyph kind={glyph} color={accent} size={22} />
        </div>
        {isMatchedOrRight ? (
          <div style={{
            width: 18, height: 18, borderRadius: '50%',
            border: `1.5px solid ${accent}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5l2 2 4-5" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        ) : (
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: C.textDim, letterSpacing: '0.18em' }}>{version}</div>
        )}
      </div>
      <div style={{ fontSize: 15, color: C.textHigh, fontWeight: 600, letterSpacing: '-0.01em' }}>{tissue}</div>
      <div style={{ fontFamily: FONT.mono, fontSize: 10, color: C.textMuted, letterSpacing: '0.16em', textTransform: 'uppercase' }}>{disease}</div>
    </div>
  );
}

function GenericSignatureCard({ state = 'idle', width = 200, height = 100 }: {
  state?: 'idle' | 'wrong' | 'highlight'; width?: number; height?: number;
}) {
  let bg = 'rgba(15,23,41,0.5)', border = 'rgba(255,255,255,0.22)', accent = 'rgba(204,251,241,0.55)', glow = 'none';
  if (state === 'wrong')          { border = 'rgba(248,113,113,0.65)'; bg = 'rgba(248,113,113,0.08)'; glow = '0 0 28px -4px rgba(248,113,113,0.55)'; accent = C.rose; }
  else if (state === 'highlight') { border = 'rgba(255,255,255,0.4)';  accent = 'rgba(254,243,199,0.85)'; }
  return (
    <div style={{ width, height, background: bg, border: `1.2px dashed ${border}`, borderRadius: 10, padding: 14,
      display: 'flex', flexDirection: 'column', gap: 6, fontFamily: FONT.sans, boxShadow: glow, boxSizing: 'border-box',
      transition: 'box-shadow 280ms, border-color 280ms, background 280ms',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <rect x="3.5" y="3.5" width="15" height="15" rx="2" stroke={accent} strokeWidth="1.4" strokeDasharray="3 2.5" fill="none"/>
          <path d="M8 11h6M11 8v6" stroke={accent} strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: accent, opacity: 0.7, letterSpacing: '0.18em' }}>DEFAULT</div>
      </div>
      <div style={{ fontSize: 15, color: accent, fontWeight: 600, letterSpacing: '-0.01em' }}>Generic signature</div>
      <div style={{ fontFamily: FONT.mono, fontSize: 10, color: accent, opacity: 0.7, letterSpacing: '0.16em', textTransform: 'uppercase' }}>No tissue context</div>
    </div>
  );
}

function ProportionsBar({ x, y, width = 820, height = 64, values, revealProgress = 1, showLabels = true, tone = 'right', scrambleProgress = 0 }: {
  x: number; y: number; width?: number; height?: number; values: number[];
  revealProgress?: number; showLabels?: boolean; tone?: string; scrambleProgress?: number;
}) {
  const segs = values.map((v, i) => {
    const segStart = i / values.length;
    const local = (revealProgress - segStart) / Math.max(0.001, 1 / values.length);
    const t = Math.max(0, Math.min(1, local));
    return v * (1 - Math.pow(1 - t, 3));
  });
  const total = Math.max(0.001, segs.reduce((a, b) => a + b, 0));
  const scrambleJitter = scrambleProgress > 0
    ? segs.map((_, i) => Math.sin(i * 7 + scrambleProgress * 20) * 0.2 * scrambleProgress)
    : segs.map(() => 0);

  return (
    <div style={{ position: 'absolute', left: x, top: y, width, fontFamily: FONT.sans }}>
      <div style={{
        display: 'flex', height, background: 'rgba(15,23,41,0.5)',
        border: `1px solid ${tone === 'wrong' ? 'rgba(248,113,113,0.4)' : C.borderStrong}`,
        borderRadius: 10, overflow: 'hidden', position: 'relative',
      }}>
        {segs.map((v, i) => {
          if (v <= 0) return null;
          const w = (v + scrambleJitter[i]) / total * 100;
          return (
            <div key={i} style={{ width: `${Math.max(0, w)}%`, background: CELL_TYPES[i].color, position: 'relative' }}>
              {tone === 'wrong' && scrambleProgress > 0.4 && (
                <div style={{ position: 'absolute', inset: 0,
                  background: 'repeating-linear-gradient(135deg, rgba(248,113,113,0.0) 0 6px, rgba(248,113,113,0.18) 6px 8px)',
                  mixBlendMode: 'overlay' }} />
              )}
            </div>
          );
        })}
        {tone === 'wrong' && scrambleProgress > 0.6 && (
          <div style={{ position: 'absolute', inset: 0, border: '1px solid rgba(248,113,113,0.7)', borderRadius: 10, boxShadow: '0 0 32px -6px rgba(248,113,113,0.6)', pointerEvents: 'none' }} />
        )}
      </div>
      {showLabels && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', marginTop: 14, opacity: Math.min(1, revealProgress * 1.4) }}>
          {CELL_TYPES.map((ct) => (
            <div key={ct.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: ct.color }} />
              <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, letterSpacing: '0.06em' }}>{ct.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContextFilters({ x, y, items, progress = 1 }: {
  x: number; y: number; items: { label: string; value: string; t: number }[]; progress?: number;
}) {
  return (
    <div style={{ position: 'absolute', left: x, top: y, display: 'flex', gap: 12, fontFamily: FONT.sans }}>
      {items.map((it, i) => {
        const local = (progress - it.t) * 4;
        const reveal = Math.max(0, Math.min(1, local));
        if (reveal <= 0) return null;
        const scale = 0.85 + 0.15 * (1 - Math.pow(1 - reveal, 3));
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px 8px 10px',
            background: 'rgba(20,184,166,0.10)', border: '1px solid rgba(94,234,212,0.45)', borderRadius: 999,
            opacity: reveal, transform: `scale(${scale})`, transformOrigin: 'left center',
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7l3 3 5-6" stroke={C.teal300} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.teal100, letterSpacing: '0.18em', textTransform: 'uppercase' }}>{it.label}</div>
            <div style={{ fontSize: 13, color: C.textHigh, fontWeight: 500 }}>{it.value}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── BarsToRings — histogram bars morph into concentric brand-mark arcs ──────
// mode='lift': bars shrink+translate toward arc center; rings grow angularly.
function BarsToRings({ values, reveal = 1, morphT = 0, mode = 'lift',
  hX, hY, hW, hMaxH, showLabels = true, showPct = true,
  arcCx, arcCy, arcInnerR = 46, arcOuterR = 200,
  arcRotBase = -90, arcRotStep = 30, arcMinSpan = 30, arcMaxSpan = 270,
  arcThickness = 5, glow = true, pulse = 0,
  cellTypes = CELL_TYPES,
  labels = HIST_LABELS,
}: {
  values: number[]; reveal?: number; morphT?: number; mode?: 'shrink' | 'bloom' | 'lift';
  hX: number; hY: number; hW: number; hMaxH: number;
  showLabels?: boolean; showPct?: boolean;
  arcCx: number; arcCy: number; arcInnerR?: number; arcOuterR?: number;
  arcRotBase?: number; arcRotStep?: number; arcMinSpan?: number; arcMaxSpan?: number;
  arcThickness?: number; glow?: boolean; pulse?: number;
  cellTypes?: { label: string; color: string; gradFrom?: string; gradTo?: string }[];
  labels?: [string, string][];
}) {
  const n = values.length;
  const GAP = Math.floor(hW * 0.04);
  const barW = Math.floor((hW - (n - 1) * GAP) / n);
  const maxVal = Math.max(...values, 0.01);

  const polarToCart = (cx: number, cy: number, r: number, deg: number) => {
    const rad = deg * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const arcPath = (cx: number, cy: number, r: number, startDeg: number, endDeg: number) => {
    const s = polarToCart(cx, cy, r, startDeg);
    const e = polarToCart(cx, cy, r, endDeg);
    const angular = Math.abs(endDeg - startDeg);
    const largeArc = angular > 180 ? 1 : 0;
    const sweep = endDeg > startDeg ? 1 : 0;
    return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r.toFixed(2)} ${r.toFixed(2)} 0 ${largeArc} ${sweep} ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
  };

  const svgPad = arcThickness + 12;
  const svgSize = (arcOuterR + svgPad) * 2;
  const svgX = arcCx - svgSize / 2;
  const svgY = arcCy - svgSize / 2;

  return (
    <>
      {/* Histogram bars + labels */}
      {values.map((v, i) => {
        const isEmpty = v < 0.005;
        const color = cellTypes[i].color;
        const fullH = (v / maxVal) * hMaxH;
        const barLeft = hX + i * (barW + GAP);
        const barCenterX = barLeft + barW / 2;

        const revealStart = (i / n) * 0.5;
        const revealLocal = Math.max(0, Math.min(1, (reveal - revealStart) / 0.55));
        const revealEased = 1 - Math.pow(1 - revealLocal, 3);
        const revealedH = fullH * revealEased;
        const revealedTop = hY + hMaxH - revealedH;

        const morphStart = i * 0.04;
        const morphLocal = Math.max(0, Math.min(1, (morphT - morphStart) / 0.75));
        const me = ss(morphLocal);

        let barH = revealedH;
        let barTop = revealedTop;
        let barOpacity = 0.88;
        let extraTransform = '';

        if (mode === 'shrink') {
          barH = revealedH * (1 - me);
          barTop = hY + hMaxH - barH;
          barOpacity = 0.88 * (1 - me * 0.3);
        } else if (mode === 'bloom') {
          barH = revealedH;
          barTop = revealedTop;
          barOpacity = 0.88 * Math.max(0, 1 - me * 1.4);
        } else if (mode === 'lift') {
          barH = revealedH * (1 - me);
          barTop = hY + hMaxH - barH;
          const dx = arcCx - barCenterX;
          const dy = arcCy - barTop;
          extraTransform = `translate(${(dx * me).toFixed(2)}px, ${(dy * me * 0.8).toFixed(2)}px)`;
          barOpacity = 0.88 * (1 - me * 0.85);
        }

        return (
          <Fragment key={`bar-${i}`}>
            {/* background track — visible while in histogram form */}
            {morphT < 0.5 && (
              <div style={{
                position: 'absolute',
                left: barLeft + 4, top: hY,
                width: barW - 8, height: hMaxH,
                background: isEmpty ? 'rgba(248,113,113,0.06)' : 'rgba(20,184,166,0.07)',
                border: isEmpty ? '1px dashed rgba(248,113,113,0.25)' : 'none',
                borderRadius: 5,
                boxSizing: 'border-box',
                opacity: Math.max(0, 1 - morphT * 2.2),
              }}/>
            )}

            {/* bar fill */}
            {!isEmpty && barH > 0 && (
              <div style={{
                position: 'absolute',
                left: barLeft + 4, top: barTop,
                width: barW - 8, height: barH,
                background: color,
                borderRadius: '4px 4px 0 0',
                opacity: barOpacity,
                transform: extraTransform,
                transformOrigin: 'center bottom',
                boxShadow: glow ? `0 0 8px 1px ${color}` : 'none',
              }}/>
            )}

            {/* percentage above bar */}
            {showPct && !isEmpty && revealLocal > 0.4 && morphT < 0.35 && (
              <div style={{
                position: 'absolute',
                top: revealedTop - 24,
                left: barLeft, width: barW,
                textAlign: 'center',
                fontFamily: FONT.mono, fontSize: 11,
                color: 'rgba(204,251,241,0.55)',
                opacity: revealLocal * Math.max(0, 1 - morphT * 3),
              }}>
                {`${Math.round(v * 100)}%`}
              </div>
            )}

            {/* cell type label below */}
            {showLabels && (
              <div style={{
                position: 'absolute',
                left: barLeft, top: hY + hMaxH + 10,
                width: barW, textAlign: 'center',
                opacity: revealEased * Math.max(0, 1 - morphT * 2),
              }}>
                <div style={{
                  fontFamily: FONT.mono, fontSize: 11, lineHeight: 1.25,
                  color: isEmpty ? 'rgba(248,113,113,0.55)' : color,
                  letterSpacing: '0.06em',
                }}>{labels[i][0]}</div>
                {labels[i][1] && (
                  <div style={{
                    fontFamily: FONT.mono, fontSize: 10, lineHeight: 1.2,
                    color: isEmpty ? 'rgba(248,113,113,0.35)' : 'rgba(204,251,241,0.38)',
                    letterSpacing: '0.06em',
                  }}>{labels[i][1]}</div>
                )}
              </div>
            )}
          </Fragment>
        );
      })}

      {/* Concentric rings (SVG) */}
      {morphT > 0.01 && (
        <svg width={svgSize} height={svgSize}
             viewBox={`0 0 ${svgSize} ${svgSize}`}
             style={{
               position: 'absolute',
               left: svgX, top: svgY,
               pointerEvents: 'none',
               overflow: 'visible',
               filter: glow ? `drop-shadow(0 0 ${(20 + morphT * 12 + pulse * 6 * morphT).toFixed(0)}px rgba(20,184,166,${(0.35 + morphT * 0.15 + pulse * 0.1 * morphT).toFixed(2)}))` : undefined,
             }}>
          <defs>
            {/* Diagonal gradient matching ArcMark's gradientUnits="userSpaceOnUse" x1=1 y1=1 x2=63 y2=63 */}
            {cellTypes.map((ct, i) =>
              ct.gradFrom && ct.gradTo ? (
                <linearGradient key={i} id={`btr-g-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1="0" y1="0" x2={svgSize} y2={svgSize}>
                  <stop offset="0%"   stopColor={ct.gradFrom} />
                  <stop offset="100%" stopColor={ct.gradTo}   />
                </linearGradient>
              ) : null
            )}
            {glow && (
              <filter id="btr-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="12"/>
                <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            )}
          </defs>

          {values.map((v, i) => {
            if (v < 0.005) return null;
            const ct = cellTypes[i];
            const strokeColor = ct.gradFrom ? `url(#btr-g-${i})` : ct.color;
            const morphStart = i * 0.04;
            const morphLocal = Math.max(0, Math.min(1, (morphT - morphStart) / 0.75));
            const me = ss(morphLocal);
            if (me <= 0) return null;

            const finalR = lerp(arcInnerR, arcOuterR, i / (n - 1));
            const fullSpan = (v / maxVal) * (arcMaxSpan - arcMinSpan) + arcMinSpan;

            let r: number, currentSpan: number;
            if (mode === 'shrink' || mode === 'lift') {
              r = finalR;
              currentSpan = fullSpan * me;
            } else {
              r = finalR * me;
              currentSpan = fullSpan * me;
            }

            if (r < 1 || currentSpan < 1) return null;

            const startAngle = arcRotBase + i * arcRotStep;
            const endAngle = startAngle + currentSpan;
            const cx = svgSize / 2;
            const cy = svgSize / 2;
            const d = arcPath(cx, cy, r, startAngle, endAngle);

            return (
              <path
                key={`arc-${i}`}
                d={d}
                fill="none"
                stroke={strokeColor}
                strokeWidth={arcThickness}
                strokeLinecap="round"
                opacity={0.4 + me * 0.6}
                filter={glow ? 'url(#btr-glow)' : undefined}
              />
            );
          })}
        </svg>
      )}
    </>
  );
}

let arcIdCounter = 0;
function ArcMark({ x, y, size = 280, progress = 1, glow = true, pulse = 0 }: {
  x: number; y: number; size?: number; progress?: number; glow?: boolean; pulse?: number;
}) {
  const idRef = useRef(++arcIdCounter);
  const prefix = `arc-${idRef.current}`;
  // Arc geometry matches CellReveal-icon-dark.svg exactly — no rotation transforms
  const arcs = [
    { r: 7,  dashArray: '32.99 10.99',   dashOffset: 10.99,  color: ['#052e16', '#115e59'] },
    { r: 13, dashArray: '61.26 20.42',   dashOffset: 13.61,  color: ['#115e59', '#0f766e'] },
    { r: 19, dashArray: '89.54 29.85',   dashOffset: 9.95,   color: ['#0f766e', '#14b8a6'] },
    { r: 25, dashArray: '117.81 39.27',  dashOffset: 0,      color: ['#14b8a6', '#d4a574'] },
    { r: 31, dashArray: '146.09 48.69',  dashOffset: -16.23, color: ['#d4a574', '#fef3c7'] },
  ];
  return (
    <div style={{ position: 'absolute', left: x, top: y, width: size, height: size,
      filter: glow ? `drop-shadow(0 0 ${20 + pulse * 12}px rgba(20,184,166,${0.35 + pulse * 0.15}))` : 'none',
    }}>
      <svg width={size} height={size} viewBox="0 0 64 64" overflow="visible">
        <defs>
          {arcs.map((a, i) => (
            <linearGradient key={i} id={`${prefix}-g-${i}`} gradientUnits="userSpaceOnUse" x1="1" y1="1" x2="63" y2="63">
              <stop offset="0%" stopColor={a.color[0]} />
              <stop offset="100%" stopColor={a.color[1]} />
            </linearGradient>
          ))}
          <filter id={`${prefix}-glow`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.8"/>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {arcs.map((a, i) => {
          const t0 = i * 0.12, t1 = t0 + 0.5;
          const local = Math.max(0, Math.min(1, (progress - t0) / (t1 - t0)));
          const eased = 1 - Math.pow(1 - local, 3);
          const dLen = parseFloat(a.dashArray.split(' ')[0]);
          const dGap = parseFloat(a.dashArray.split(' ')[1]);
          const visible = dLen * eased;
          const hidden = dLen * (1 - eased) + dGap;
          return (
            <circle key={i} cx="32" cy="32" r={a.r} fill="none"
              stroke={`url(#${prefix}-g-${i})`} strokeWidth="2.2"
              strokeLinecap="round"
              strokeDasharray={`${visible} ${hidden}`}
              strokeDashoffset={a.dashOffset}
              filter={`url(#${prefix}-glow)`}
              opacity={0.4 + local * 0.6}
            />
          );
        })}
      </svg>
    </div>
  );
}

function Connector({ from, to, color = C.teal400, dashed = false, progress = 1, width = 1.5 }: {
  from: { x: number; y: number }; to: { x: number; y: number };
  color?: string; dashed?: boolean; progress?: number; width?: number;
}) {
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx) * 180 / Math.PI;
  const visible = len * Math.max(0, Math.min(1, progress));
  return (
    <div style={{
      position: 'absolute', left: from.x, top: from.y,
      width: visible, height: 0,
      borderTop: `${width}px ${dashed ? 'dashed' : 'solid'} ${color}`,
      transformOrigin: '0 0',
      transform: `rotate(${angle}deg)`,
      pointerEvents: 'none',
    }} />
  );
}

function Eyebrow({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: color || C.textDim, fontWeight: 400 }}>
      {children}
    </div>
  );
}

// ─── Mini components shared across scenes ─────────────────────────────────────

function MiniSampleMatrix({ x, y, opacity = 0.55 }: { x: number; y: number; opacity?: number }) {
  return (
    <div style={{ position: 'absolute', left: x, top: y, opacity }}>
      <MatrixGrid x={0} y={0} cols={10} rows={20} cell={8} gap={1} noiseSeed={11} />
    </div>
  );
}

function MiniCaption({ x, y, color = C.textDim, opacity = 1 }: { x: number; y: number; color?: string; opacity?: number }) {
  return (
    <div style={{ position: 'absolute', left: x, top: y, fontFamily: FONT.mono, fontSize: 10, color, opacity,
      letterSpacing: '0.18em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M5 1v8M2 6l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Expression values
    </div>
  );
}

// ─── Stage background ─────────────────────────────────────────────────────────

function StageBackground() {
  const time = useTime();
  const drift = Math.sin(time * 0.4) * 60;
  return (
    <div style={{ position: 'absolute', inset: 0, background: C.navy950, overflow: 'hidden' }}>
      <GridBackground opacity={0.045} tile={72} />
      <div style={{ position: 'absolute', left: STAGE_W * 0.15 + drift, top: STAGE_H * 0.3,
        width: 800, height: 800, borderRadius: '50%', background: 'rgba(20,184,166,0.05)', filter: 'blur(120px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: STAGE_W * 0.05 - drift, bottom: 0,
        width: 700, height: 700, borderRadius: '50%', background: 'rgba(212,165,116,0.04)', filter: 'blur(120px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, transparent 40%, rgba(2,24,22,0.4) 100%)', pointerEvents: 'none' }} />
    </div>
  );
}

function CornerMark({ brandSceneStart }: { brandSceneStart: number }) {
  const time = useTime();
  const fadeOut = Math.max(0, Math.min(1, (brandSceneStart - 0.3 - time) / 0.4));
  return (
    <div style={{ position: 'absolute', left: 64, top: 56, display: 'flex', alignItems: 'center', gap: 14, opacity: 0.85 * fadeOut, pointerEvents: 'none' }}>
      <div style={{ position: 'relative', width: 40, height: 40 }}>
        <ArcMark x={0} y={0} size={40} progress={1} glow={false} />
      </div>
      <div style={{ display: 'flex', fontFamily: FONT.display, fontSize: 24, lineHeight: 1 }}>
        <span style={{ fontWeight: 300, color: C.teal100, letterSpacing: '-0.022em' }}>Cell</span>
        <span style={{ fontWeight: 700, color: C.cream100, letterSpacing: '-0.042em' }}>Reveal</span>
      </div>
    </div>
  );
}

// ─── Scenes ───────────────────────────────────────────────────────────────────

function SceneData() {
  const { localTime, duration } = useSprite();
  const reveal      = ss(localTime / 1.6);
  const scan        = Math.max(0, Math.min(1, (localTime - 0.4) / 2.0));
  const pulsePhase  = localTime * 2.2;
  const exitFade    = Math.max(0, Math.min(1, (duration - localTime) / 0.5));
  const zoom        = 1 + ss(localTime / duration) * 0.04;
  const captionIn   = ss((localTime - 0.2) / 0.7);

  const cols = 26, rows = 64, cell = 11, gap = 1;
  const w = cols * cell + (cols - 1) * gap;
  const h = rows * cell + (rows - 1) * gap;
  const mx = 280, my = (STAGE_H - h) / 2 - 30;

  return (
    <div style={{ position: 'absolute', inset: 0, opacity: exitFade }}>
      <Halo x={mx + w / 2} y={my + h / 2} size={1100} color="rgba(20,184,166,0.20)" opacity={ss(localTime / 1.2)} />

      <div style={{ position: 'absolute', left: mx + w + 200, top: my + h / 2 - 130, opacity: captionIn, transform: `translateY(${(1 - captionIn) * 12}px)`, maxWidth: 1100 }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.55)' }}>— INPUT</div>
        <div style={{ fontFamily: FONT.display, fontSize: 96, fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.025em', color: C.textHigh, marginTop: 18 }}>Bulk RNA-seq.</div>
        <div style={{ fontFamily: FONT.display, fontSize: 64, fontWeight: 300, lineHeight: 1.04, letterSpacing: '-0.022em', color: C.textMuted, marginTop: 16 }}>
          One <span style={{ color: C.amber300, fontWeight: 700 }}>mixed</span> signal.
        </div>
      </div>

      <div style={{ position: 'absolute', left: mx - 60, top: my + h / 2 - 50, fontFamily: FONT.mono, fontSize: 11, color: C.textDim, letterSpacing: '0.16em', writingMode: 'vertical-rl', transform: 'rotate(180deg)', opacity: ss((localTime - 0.6) / 0.6) }}>
        GENES · 24,802
      </div>
      <div style={{ position: 'absolute', left: mx + w / 2 - 40, top: my + h + 22, fontFamily: FONT.mono, fontSize: 11, color: C.textDim, letterSpacing: '0.16em', opacity: ss((localTime - 0.6) / 0.6) }}>
        SAMPLES · 84
      </div>

      <div style={{ transform: `scale(${zoom})`, transformOrigin: `${mx + w / 2}px ${my + h / 2}px` }}>
        <MatrixGrid x={mx} y={my} cols={cols} rows={rows} cell={cell} gap={gap}
          revealProgress={reveal} scanLine scanProgress={scan} pulse pulsePhase={pulsePhase} noiseSeed={11} />
      </div>
    </div>
  );
}

function SceneQuestion() {
  const { localTime, duration } = useSprite();
  const chipIn    = ss(localTime / 0.7);
  const libStart  = 0.4;
  const captionIn = ss((localTime - 0.1) / 0.6);
  const miniIn    = ss((localTime - 0.5) / 0.6);
  const exitFade  = Math.max(0, Math.min(1, (duration - localTime) / 0.5));

  return (
    <div style={{ position: 'absolute', inset: 0, opacity: exitFade }}>
      <Halo x={STAGE_W * 0.5} y={STAGE_H / 2} size={1000} color="rgba(20,184,166,0.10)" opacity={1} />

      <div style={{ position: 'absolute', left: CAP_X, top: CAP_Y, opacity: captionIn, transform: `translateY(${(1 - captionIn) * 10}px)` }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.55)' }}>— THE QUESTION</div>
        <div style={{ fontFamily: FONT.display, fontSize: 80, fontWeight: 700, lineHeight: 1.0, letterSpacing: '-0.025em', color: C.textHigh, marginTop: 18 }}>
          Which cells<br/>
          <span style={{ fontWeight: 300, color: C.textMuted }}>are inside?</span>
        </div>
      </div>

      <div style={{ position: 'absolute', left: SAMPLE_X + (1 - chipIn) * -80, top: SAMPLE_Y, opacity: chipIn }}>
        <SampleChip x={0} y={0} scale={1.3} />
      </div>

      <div style={{ opacity: miniIn }}>
        <MiniCaption x={SAMPLE_X} y={MINI_Y - 24} />
        <MiniSampleMatrix x={SAMPLE_X} y={MINI_Y} />
      </div>

      <div style={{ position: 'absolute', left: LIB_X, top: LIB_Y - 56, opacity: ss((localTime - libStart) / 0.6) }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.55)' }}>— SIGNATURE LIBRARY · 50+ CONTEXTS</div>
      </div>

      {LIBRARY_DATA.map((sig, i) => {
        const p = libCardPos(i);
        const t0 = libStart + i * 0.07;
        const r  = Math.max(0, Math.min(1, (localTime - t0) / 0.5));
        if (r <= 0) return null;
        const eased = 1 - Math.pow(1 - r, 3);
        return (
          <div key={i} style={{ position: 'absolute', left: p.x, top: p.y, opacity: eased, transform: `translateY(${(1 - eased) * 16}px) scale(${0.92 + 0.08 * eased})`, transformOrigin: 'center' }}>
            <SignatureCard {...sig} width={CARD_W} height={CARD_H} />
          </div>
        );
      })}
    </div>
  );
}

function SceneWrong() {
  const { localTime, duration } = useSprite();
  const exitFade    = Math.max(0, Math.min(1, (duration - localTime) / 0.5));
  const captionIn   = ss(localTime / 0.5);
  const libDim      = ss(localTime / 0.6);
  const moveT       = ss((localTime - 0.3) / 0.9);
  const dockT       = ss((localTime - 1.1) / 0.7);
  const scrambleT   = ss((localTime - 1.7) / 0.6);
  const errorT      = ss((localTime - 2.2) / 0.5);

  const targetPos = { x: SAMPLE_X + 440, y: SAMPLE_Y - 6 };
  const startPos  = { x: targetPos.x,    y: STAGE_H + 40 };
  const cardX     = lerp(startPos.x, targetPos.x, moveT);
  const cardY     = lerp(startPos.y, targetPos.y, moveT);
  const cardScale = lerp(0.92, 1.18, moveT) * (1 - scrambleT * 0.03);
  const wiggle    = scrambleT > 0 ? Math.sin(localTime * 32) * scrambleT * 3 : 0;

  return (
    <div style={{ position: 'absolute', inset: 0, opacity: exitFade }}>
      <Halo x={STAGE_W * 0.5} y={STAGE_H / 2} size={1000}
        color={errorT > 0 ? `rgba(248,113,113,${0.22 * errorT})` : 'rgba(20,184,166,0.08)'} opacity={1} />

      <div style={{ position: 'absolute', left: CAP_X, top: CAP_Y, opacity: captionIn * exitFade }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: errorT > 0 ? 'rgba(248,113,113,0.7)' : 'rgba(94,234,212,0.55)', transition: 'color 320ms' }}>— THE CATCH</div>
        <div style={{ fontFamily: FONT.display, fontSize: 80, fontWeight: 700, lineHeight: 1.0, letterSpacing: '-0.025em', color: C.textHigh, marginTop: 18 }}>
          <span style={{ color: C.textMuted, fontWeight: 300 }}>A generic signature</span><br/>
          <span style={{ color: errorT > 0 ? C.rose : C.textHigh, fontWeight: 700 }}>misreads the tissue.</span>
        </div>
      </div>

      <SampleChip x={SAMPLE_X} y={SAMPLE_Y} scale={1.3} tone={scrambleT > 0.3 ? 'wrong' : 'default'} />
      <div style={{ opacity: captionIn }}>
        <MiniCaption x={SAMPLE_X} y={MINI_Y - 24} />
        <MiniSampleMatrix x={SAMPLE_X} y={MINI_Y} />
      </div>

      {LIBRARY_DATA.map((sig, i) => {
        const p = libCardPos(i);
        return (
          <div key={i} style={{ position: 'absolute', left: p.x, top: p.y, opacity: 1 - 0.65 * libDim, transition: 'opacity 320ms cubic-bezier(0.16,1,0.3,1)' }}>
            <SignatureCard {...sig} width={CARD_W} height={CARD_H} state="dim" />
          </div>
        );
      })}

      {libDim > 0.3 && (
        <div style={{ position: 'absolute', left: LIB_X, top: LIB_Y - 56, opacity: libDim }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(204,251,241,0.25)' }}>— SIGNATURE LIBRARY · SKIPPED</div>
        </div>
      )}

      <div style={{ position: 'absolute', left: cardX + wiggle, top: cardY + wiggle, transform: `scale(${cardScale})`, transformOrigin: 'top left', zIndex: 5 }}>
        <GenericSignatureCard width={CARD_W} height={CARD_H} state={scrambleT > 0.2 ? 'wrong' : 'highlight'} />
      </div>

      {dockT > 0 && (
        <Connector
          from={{ x: SAMPLE_X + 390, y: SAMPLE_Y + 32 }}
          to={{ x: targetPos.x, y: targetPos.y + CARD_H / 2 }}
          color={scrambleT > 0.3 ? C.rose : 'rgba(255,255,255,0.35)'}
          dashed
          progress={dockT}
        />
      )}

      {dockT > 0.2 && (
        <div style={{ position: 'absolute', left: BAR_X, top: BAR_Y, opacity: ss((dockT - 0.2) / 0.5) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <Eyebrow color="rgba(248,113,113,0.6)">— OUTPUT</Eyebrow>
            {errorT > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: errorT }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(248,113,113,0.18)', border: '1px solid rgba(248,113,113,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT.mono, fontSize: 13, color: C.rose, fontWeight: 700 }}>!</div>
                <div style={{ fontFamily: FONT.mono, fontSize: 12, color: 'rgba(248,113,113,0.85)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>NO CONTEXT · 4 CELL TYPES MISSING</div>
              </div>
            )}
          </div>
          <ProportionsBar x={0} y={0} width={BAR_W} height={56} values={WRONG_PROPORTIONS} revealProgress={dockT} tone="wrong" scrambleProgress={scrambleT} showLabels={false} />
        </div>
      )}
    </div>
  );
}

const RIGHT_IDX = 0;

function SceneRight() {
  const { localTime, duration } = useSprite();
  const exitFade  = Math.max(0, Math.min(1, (duration - localTime) / 0.6));
  const captionIn = ss(localTime / 0.5);
  const filterT   = ss(localTime / 1.0);
  const matchT    = ss((localTime - 1.0) / 0.8);
  const slideT    = ss((localTime - 1.7) / 0.9);
  const barT      = ss((localTime - 2.3) / 0.8);
  const labelsT   = ss((localTime - 2.7) / 0.5);

  const ACT2 = 3.6;
  const p2   = Math.max(0, localTime - ACT2);
  const tT   = ss(p2 / 1.0);
  const captionMorph = ss((tT - 0.3) / 0.4);

  const pairT = ACT2_MATCHES.map((_m, i) => {
    if (i === 0) return 1;
    return ss((p2 - 0.7 - (i - 1) * 0.35) / 0.55);
  });
  const aggT = ss((p2 - 2.4) / 0.6);

  const lungSampleY     = lerp(SAMPLE_Y, ROW_Y[0], tT);
  const lungSampleScale = lerp(1.3, 1.0, tT);

  const lungCard_lib  = libCardPos(RIGHT_IDX);
  const lungCard_dock = { x: SAMPLE_X + 440, y: SAMPLE_Y - 6 };
  const lungCard_row  = { x: CARD_X, y: ROW_Y[0] + CARD_MID_OFFSET_Y };
  const lungCard_act1X = lerp(lungCard_lib.x, lungCard_dock.x, slideT);
  const lungCard_act1Y = lerp(lungCard_lib.y, lungCard_dock.y, slideT);
  const lungCardX      = lerp(lungCard_act1X, lungCard_row.x, tT);
  const lungCardY      = lerp(lungCard_act1Y, lungCard_row.y, tT);
  const lungCardScale  = lerp(lerp(1, 1.22, slideT), 1.0, tT);

  return (
    <div style={{ position: 'absolute', inset: 0, opacity: exitFade }}>
      <Halo x={STAGE_W * 0.5} y={STAGE_H / 2} size={1100} color="rgba(20,184,166,0.20)" opacity={Math.min(1, 0.5 + matchT * 0.5)} />

      {/* Caption */}
      <div style={{ position: 'absolute', left: CAP_X, top: 160, opacity: captionIn }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.55)', position: 'relative' }}>
          <div style={{ opacity: 1 - captionMorph }}>— THE FIX</div>
          <div style={{ position: 'absolute', inset: 0, opacity: captionMorph }}>— EVERY CONTEXT</div>
        </div>
        <div style={{ fontFamily: FONT.display, fontSize: 80, fontWeight: 700, lineHeight: 1.04, letterSpacing: '-0.025em', color: C.textHigh, marginTop: 14, whiteSpace: 'nowrap', position: 'relative' }}>
          <div style={{ opacity: 1 - captionMorph }}>
            <span style={{ fontWeight: 300, color: C.textMuted }}>Match the </span><span style={{ color: C.teal300 }}>context.</span>
          </div>
          <div style={{ position: 'absolute', top: 0, left: 0, opacity: captionMorph }}>
            <span style={{ fontWeight: 300, color: C.textMuted }}>Every context, </span><span style={{ color: C.teal300 }}>matched.</span>
          </div>
        </div>
        <div style={{ position: 'relative', marginTop: lerp(20, 0, tT), height: lerp(44, 0, tT), opacity: 1 - tT, overflow: 'hidden' }}>
          <ContextFilters x={0} y={0} items={[
            { label: 'Tissue', value: 'Lung', t: 0.0 },
            { label: 'Disease', value: 'Tumor', t: 0.25 },
            { label: 'Cohort', value: 'NSCLC', t: 0.5 },
          ]} progress={filterT} />
        </div>
      </div>

      {/* Mini matrix (Act 1 only) */}
      <div style={{ opacity: 1 - tT }}>
        <MiniCaption x={SAMPLE_X} y={MINI_Y - 24} />
        <MiniSampleMatrix x={SAMPLE_X} y={MINI_Y} />
      </div>

      {/* Big bar (Act 1 only) */}
      {barT > 0 && (
        <div style={{ position: 'absolute', left: BAR_X, top: BAR_Y, opacity: Math.max(0, 1 - tT * 1.6) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <Eyebrow color="rgba(94,234,212,0.55)">— CELL-TYPE PROPORTIONS</Eyebrow>
            <div style={{ fontFamily: FONT.mono, fontSize: 12, letterSpacing: '0.18em', color: C.teal300, opacity: ss((barT - 0.5) / 0.4), display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: `1.5px solid ${C.teal300}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="9" height="9" viewBox="0 0 8 8"><path d="M1.5 4l1.5 1.5L6.5 2" stroke={C.teal300} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              VALIDATED · CITABLE
            </div>
          </div>
          <ProportionsBar x={0} y={0} width={BAR_W} height={56} values={RIGHT_PROPORTIONS} revealProgress={barT} tone="right" showLabels={labelsT > 0} />
        </div>
      )}

      {/* Library */}
      {LIBRARY_DATA.map((sig, i) => {
        if (i === RIGHT_IDX) return null;
        const matchMeta = ACT2_MATCHES.find(m => m.libIdx === i);
        if (matchMeta) {
          const libP = libCardPos(i);
          const rowP = { x: CARD_X, y: ROW_Y[matchMeta.row] + CARD_MID_OFFSET_Y };
          const pt   = pairT[matchMeta.row];
          const x    = lerp(libP.x, rowP.x, pt);
          const y    = lerp(libP.y, rowP.y, pt);
          const state = pt > 0.3 ? 'matched' : (tT > 0.4 ? 'dim' : 'idle');
          return (
            <div key={i} style={{ position: 'absolute', left: x, top: y, transition: 'opacity 320ms cubic-bezier(0.16,1,0.3,1)', zIndex: 4 }}>
              <SignatureCard {...sig} width={CARD_W} height={CARD_H} state={state as 'matched' | 'dim' | 'idle'} />
            </div>
          );
        }
        const p = libCardPos(i);
        return (
          <div key={i} style={{ position: 'absolute', left: p.x, top: p.y, opacity: (1 - tT) * (matchT > 0.4 ? 0.6 : 1), transition: 'opacity 320ms cubic-bezier(0.16,1,0.3,1)' }}>
            <SignatureCard {...sig} width={CARD_W} height={CARD_H} state="dim" />
          </div>
        );
      })}

      {/* Lung card */}
      <div style={{ position: 'absolute', left: lungCardX, top: lungCardY, transform: `scale(${lungCardScale})`, transformOrigin: 'top left', zIndex: 5 }}>
        <SignatureCard {...LIBRARY_DATA[RIGHT_IDX]} width={CARD_W} height={CARD_H} state={matchT > 0.4 ? 'matched' : 'idle'} />
      </div>

      {/* Sample chips */}
      <div style={{ position: 'absolute', left: CHIP_X, top: lungSampleY }}>
        <SampleChip x={0} y={0} scale={lungSampleScale} label="SAMPLE_042" sublabel="Lung tumor biopsy" tone={matchT > 0.3 ? 'right' : 'default'} width={CHIP_W} />
      </div>
      {ACT2_MATCHES.slice(1).map((m, idx) => {
        const i  = idx + 1;
        const pt = pairT[i];
        if (pt <= 0) return null;
        const startX = -CHIP_W - 60;
        const x = lerp(startX, CHIP_X, pt);
        return (
          <div key={`chip-${i}`} style={{ position: 'absolute', left: x, top: ROW_Y[i], opacity: ss(pt * 1.3) }}>
            <SampleChip x={0} y={0} scale={1.0} label={m.label} sublabel={m.sublabel} tone={pt > 0.4 ? 'right' : 'default'} width={CHIP_W} />
          </div>
        );
      })}

      {/* Connectors */}
      {ACT2_MATCHES.map((_m, i) => {
        const pt = pairT[i];
        if (i === 0) {
          if (slideT < 0.3 && tT === 0) return null;
          if (tT < 0.05) {
            const chipR  = SAMPLE_X + CHIP_W * 1.3 + 6;
            const chipMy = SAMPLE_Y + 38 * 1.3;
            return (
              <Connector key={`c-${i}`}
                from={{ x: chipR, y: chipMy }}
                to={{ x: lungCard_dock.x, y: lungCard_dock.y + CARD_H / 2 }}
                color="rgba(94,234,212,0.55)"
                progress={ss((slideT - 0.3) / 0.6)}
                width={1.5}
              />
            );
          }
          const chipR  = CHIP_X + CHIP_W * lungSampleScale + 6;
          const my     = lungSampleY + 38 * lungSampleScale;
          return (
            <Connector key={`c-${i}`}
              from={{ x: chipR, y: my }}
              to={{ x: lungCardX - 6, y: lungCardY + CARD_H / 2 }}
              color="rgba(94,234,212,0.55)"
              progress={1}
              width={1.5}
            />
          );
        }
        if (pt <= 0.4) return null;
        return (
          <Connector key={`c-${i}`}
            from={{ x: CHIP_X + CHIP_W + 6, y: ROW_MID_Y(i) }}
            to={{ x: CARD_X - 6, y: ROW_Y[i] + CARD_H / 2 + CARD_MID_OFFSET_Y }}
            color="rgba(94,234,212,0.55)"
            progress={ss((pt - 0.4) / 0.5)}
            width={1.5}
          />
        );
      })}

      {/* Aggregate footer */}
      {aggT > 0 && (
        <div style={{ position: 'absolute', left: CAP_X, top: STAGE_H - 130, opacity: aggT, transform: `translateY(${(1 - aggT) * 8}px)`, display: 'flex', alignItems: 'baseline', gap: 24 }}>
          <Eyebrow color="rgba(94,234,212,0.55)">— PARALLEL MATCHES</Eyebrow>
          <div style={{ fontFamily: FONT.display, fontSize: 26, color: C.textHigh, fontWeight: 600, letterSpacing: '-0.015em' }}>
            4 contexts <span style={{ color: C.textMuted, fontWeight: 300 }}>·</span>{' '}
            4 signatures <span style={{ color: C.textMuted, fontWeight: 300 }}>·</span>{' '}
            <span style={{ color: C.teal300 }}>one click.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Engine internal motion — concentric rotating rings + particle flow ───────
function EngineMotion({ w, h, motionT, reveal }: { w: number; h: number; motionT: number; reveal: number }) {
  const cx = w / 2, cy = h / 2;
  const N = 26;
  const particles = useMemo(() => {
    return Array.from({ length: N }, (_, i) => {
      const speed = 0.5 + (i % 5) * 0.05;
      const phase = i / N;
      const lane = ((i * 7) % 5) - 2;
      return { speed, phase, lane };
    });
  }, []);

  const rings = [
    { r: 130, dash: '6 10',  color: 'rgba(20,184,166,0.4)',   rate:  10, w: 1.2 },
    { r: 105, dash: '14 6',  color: 'rgba(45,212,191,0.55)',  rate: -16, w: 1.4 },
    { r:  80, dash: '4 8',   color: 'rgba(94,234,212,0.7)',   rate:  22, w: 1.4 },
    { r:  55, dash: '20 10', color: 'rgba(212,165,116,0.55)', rate: -28, w: 1.4 },
    { r:  32, dash: '6 4',   color: 'rgba(254,243,199,0.65)', rate:  38, w: 1.4 },
  ];

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}
         style={{ position: 'absolute', inset: 0, opacity: reveal }}>
      <defs>
        <radialGradient id="engine-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={C.cream100} stopOpacity="0.9"/>
          <stop offset="40%"  stopColor={C.teal300}  stopOpacity="0.45"/>
          <stop offset="100%" stopColor={C.teal700}  stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="engine-particle" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={C.teal300} stopOpacity="0"/>
          <stop offset="50%"  stopColor={C.teal300} stopOpacity="0.95"/>
          <stop offset="100%" stopColor={C.cream100} stopOpacity="0"/>
        </linearGradient>
      </defs>

      {/* soft core glow */}
      <circle cx={cx} cy={cy} r={140}
              fill="url(#engine-core)"
              opacity={0.55 + 0.15 * Math.sin(motionT * 1.6)}/>

      {/* concentric dashed rings, each rotating at its own rate */}
      {rings.map((ring, i) => (
        <circle key={i}
          cx={cx} cy={cy} r={ring.r}
          fill="none" stroke={ring.color} strokeWidth={ring.w}
          strokeDasharray={ring.dash}
          transform={`rotate(${motionT * ring.rate} ${cx} ${cy})`}/>
      ))}

      {/* pulsing center dot */}
      <circle cx={cx} cy={cy} r={5 + 1.5 * Math.sin(motionT * 4)}
              fill={C.cream100}
              opacity={0.7 + 0.25 * Math.sin(motionT * 4)}/>

      {/* particles — small dots streaming left→right through the core */}
      {particles.map((p, i) => {
        const t = (motionT * p.speed + p.phase) % 1;
        const x = t * w;
        const arc = Math.sin(t * Math.PI);
        const y = cy + p.lane * 36 * (1 - arc * 0.7);
        const env = Math.sin(t * Math.PI);
        return (
          <circle key={i}
            cx={x} cy={y} r={1.6 + env * 1.4}
            fill={C.teal300}
            opacity={env * 0.85}/>
        );
      })}
    </svg>
  );
}

// ─── Scene F — The Engine ─────────────────────────────────────────────────────
// Layered stack: bottom slab = context-matched signature (recap of D),
// top slab = CellReveal deconvolution engine with internal motion.
// Pipeline: [sample + signature] → [stacked module] → [histogram → rings morph].
function SceneAlgorithm() {
  const { localTime, duration } = useSprite();
  const exitFade    = Math.max(0, Math.min(1, (duration - localTime) / 0.5));

  // Beat timing
  const captionIn   = ss(localTime / 0.6);
  const lowerIn     = ss((localTime - 0.4) / 0.7);
  const upperIn     = ss((localTime - 0.9) / 0.9);
  const inputsIn    = ss((localTime - 1.2) / 0.7);
  const inflowT     = ss((localTime - 1.6) / 0.5);
  const motionT     = Math.max(0, localTime - 1.6);
  const outflowT    = ss((localTime - 2.4) / 0.5);
  const outIn       = ss((localTime - 2.6) / 0.9);
  const credIn      = ss((localTime - 3.0) / 0.6);
  const morphT      = ss((localTime - 3.5) / 1.5);
  const supportFade = 1 - ss((localTime - 3.4) / 0.9);
  // Text reveal after morph completes (morphT=1 at localTime≈5.0)
  const wmT         = ss((localTime - 5.3) / 0.8);
  const tagT        = ss((localTime - 5.9) / 0.8);
  const pulse       = Math.sin(localTime * 1.6) * 0.5 + 0.5;

  // Layout
  const STK_X = 540, STK_W = 680;
  const UPPER_Y = 340, UPPER_H = 300;
  const GAP = 10;
  const LOWER_Y = UPPER_Y + UPPER_H + GAP;
  const LOWER_H = 76;

  const IN_X = 140;
  const CHIP_Y = 410;
  const SIG_Y  = 560;
  const IN_W   = 300;

  const OUT_X     = STK_X + STK_W + 80;       // 1300
  const OUT_W     = STAGE_W - OUT_X - 60;     // 560
  const OUT_BAR_Y = 360;
  const OUT_BAR_H = 200;

  // Arc target — matches Scene E's brand mark center.
  // Scene E: arcSize=420, arcX=(1920-420)/2=750, arcY=1080/2-210-100=230 → center (960,440)
  const ARC_CX      = STAGE_W / 2;       // 960
  const ARC_CY      = STAGE_H / 2 - 100; // 440
  const ARC_INNER_R = 46;
  const ARC_OUTER_R = 203; // matches ArcMark outermost ring at size=420 (r=31 × 6.5625)

  return (
    <div style={{ position: 'absolute', inset: 0, opacity: exitFade }}>
      <Halo x={STAGE_W / 2} y={STAGE_H / 2} size={1300}
            color="rgba(20,184,166,0.18)" opacity={ss(localTime / 1.0)}/>

      {/* Support UI — fades out as morph begins */}
      <div style={{ opacity: supportFade, position: 'absolute', inset: 0 }}>

        {/* Caption */}
        <div style={{
          position: 'absolute',
          left: CAP_X, top: 120,
          opacity: captionIn,
          transform: `translateY(${(1 - captionIn) * 10}px)`,
          maxWidth: STAGE_W - CAP_X * 2,
        }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 14, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.55)' }}>— THE ENGINE</div>
          <div style={{ fontFamily: FONT.display, fontSize: 56, fontWeight: 700, lineHeight: 1.04, letterSpacing: '-0.025em', color: C.textHigh, marginTop: 14, whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 300, color: C.textMuted }}>On top: </span>
            <span style={{ color: C.teal300 }}>our deconvolution engine.</span>
          </div>
        </div>

        {/* Input: sample chip */}
        <div style={{
          position: 'absolute', left: IN_X, top: CHIP_Y,
          opacity: inputsIn,
          transform: `translateX(${(1 - inputsIn) * -30}px)`,
        }}>
          <SampleChip x={0} y={0} scale={1.0}
                      label="SAMPLE_042" sublabel="Lung tumor biopsy"
                      tone="right" width={IN_W}/>
        </div>

        {/* Input: matched signature card */}
        <div style={{
          position: 'absolute', left: IN_X, top: SIG_Y,
          opacity: ss((inputsIn - 0.2) * 1.5),
          transform: `translateX(${(1 - inputsIn) * -30}px)`,
        }}>
          <SignatureCard glyph="lung" tissue="Lung" disease="NSCLC · Tumor" version="v3.1"
                         width={IN_W} height={CARD_H} state="matched"/>
        </div>

        {/* Inflow connectors */}
        {inflowT > 0 && (
          <>
            <Connector
              from={{ x: IN_X + IN_W + 8, y: CHIP_Y + 38 }}
              to={{ x: STK_X - 8, y: UPPER_Y + UPPER_H / 2 }}
              color="rgba(94,234,212,0.45)"
              progress={inflowT} width={1.5}
            />
            <Connector
              from={{ x: IN_X + IN_W + 8, y: SIG_Y + CARD_H / 2 }}
              to={{ x: STK_X - 8, y: UPPER_Y + UPPER_H / 2 }}
              color="rgba(94,234,212,0.45)"
              progress={inflowT} width={1.5}
            />
          </>
        )}

        {/* Lower slab — context-matched signature recap */}
        <div style={{
          position: 'absolute',
          left: STK_X, top: LOWER_Y,
          width: STK_W, height: LOWER_H,
          background: 'rgba(15,23,41,0.55)',
          border: '1px solid rgba(94,234,212,0.22)',
          borderRadius: 12,
          padding: '0 28px',
          display: 'flex', alignItems: 'center', gap: 18,
          boxSizing: 'border-box',
          opacity: lowerIn,
          transform: `translateY(${(1 - lowerIn) * 16}px)`,
          fontFamily: FONT.sans,
        }}>
          <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ArcMark x={0} y={0} size={32} progress={1} glow={false}/>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT.mono, fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(204,251,241,0.35)' }}>— LAYER 01</div>
            <div style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 600, color: C.teal100, marginTop: 2, letterSpacing: '-0.01em' }}>Context-matched signature</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: 2,
                background: i === 0 ? C.teal300 : 'rgba(94,234,212,0.18)',
              }}/>
            ))}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 11, letterSpacing: '0.18em', color: C.teal300, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-6" stroke={C.teal300} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            LUNG · NSCLC
          </div>
        </div>

        {/* Upper slab — the deconvolution engine */}
        <div style={{
          position: 'absolute',
          left: STK_X, top: UPPER_Y,
          width: STK_W, height: UPPER_H,
          background: 'rgba(15,23,41,0.78)',
          border: '1px solid rgba(94,234,212,0.42)',
          borderRadius: 16,
          overflow: 'hidden',
          opacity: upperIn,
          transform: `translateY(${(1 - upperIn) * 24}px)`,
          boxShadow: `0 0 ${60 * upperIn}px -12px rgba(20,184,166,${0.35 * upperIn})`,
        }}>
          <EngineMotion w={STK_W} h={UPPER_H} motionT={motionT} reveal={upperIn}/>
          <div style={{ position: 'absolute', left: 26, top: 22, fontFamily: FONT.mono, fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(254,243,199,0.55)' }}>— LAYER 02</div>
          <div style={{ position: 'absolute', left: 26, top: 42, fontFamily: FONT.display, fontSize: 22, fontWeight: 700, color: C.cream100, letterSpacing: '-0.01em' }}>CellReveal deconvolution engine</div>
          <div style={{
            position: 'absolute', left: 26, bottom: 22,
            fontFamily: FONT.mono, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(94,234,212,0.45)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: C.teal300,
              boxShadow: `0 0 8px ${C.teal300}`,
              opacity: 0.6 + 0.4 * Math.sin(motionT * 3),
            }}/>
            DECONVOLVING
          </div>
        </div>

        {/* Outflow connector */}
        {outflowT > 0 && (
          <Connector
            from={{ x: STK_X + STK_W + 8, y: UPPER_Y + UPPER_H / 2 }}
            to={{ x: OUT_X - 8, y: OUT_BAR_Y + OUT_BAR_H / 2 }}
            color="rgba(94,234,212,0.55)"
            progress={outflowT} width={1.5}
          />
        )}

        {/* Output column label */}
        <div style={{ position: 'absolute', left: OUT_X, top: OUT_BAR_Y - 36, width: OUT_W, opacity: outIn }}>
          <Eyebrow color="rgba(94,234,212,0.55)">— CELL-TYPE PROPORTIONS</Eyebrow>
        </div>

        {/* Footer credit */}
        <div style={{
          position: 'absolute',
          left: CAP_X, top: STAGE_H - 130,
          opacity: credIn,
          transform: `translateY(${(1 - credIn) * 8}px)`,
          display: 'flex', alignItems: 'baseline', gap: 24,
        }}>
          <Eyebrow color="rgba(94,234,212,0.55)">— PEDIGREE</Eyebrow>
          <div style={{ fontFamily: FONT.display, fontSize: 24, fontWeight: 500, color: C.textBody, letterSpacing: '-0.012em' }}>
            Designed by{' '}
            <span style={{ color: C.textHigh, fontWeight: 700 }}>deconvolution experts.</span>
          </div>
        </div>
      </div>

      {/* Histogram → arc mark morph (always rendered once outIn > 0) */}
      <BarsToRings
        values={LOGO_PROPORTIONS}
        cellTypes={LOGO_RINGS}
        labels={LOGO_LABELS}
        reveal={outIn}
        morphT={morphT}
        mode="lift"
        hX={OUT_X}
        hY={OUT_BAR_Y}
        hW={OUT_W}
        hMaxH={OUT_BAR_H}
        showLabels={true}
        showPct={true}
        arcCx={ARC_CX}
        arcCy={ARC_CY}
        arcInnerR={ARC_INNER_R}
        arcOuterR={ARC_OUTER_R}
        arcRotBase={270}
        arcRotStep={30}
        arcMinSpan={270}
        arcMaxSpan={270}
        arcThickness={14}
        pulse={pulse}
      />

      {/* Wordmark — fades in once rings are fully formed */}
      <div style={{
        position: 'absolute', left: 0, right: 0,
        top: ARC_CY + ARC_OUTER_R + 50,
        textAlign: 'center',
        opacity: wmT,
        transform: `translateY(${(1 - wmT) * 12}px)`,
      }}>
        <div style={{ fontFamily: FONT.display, fontSize: 110, lineHeight: 1, display: 'inline-flex' }}>
          <span style={{ fontWeight: 300, color: C.teal100, letterSpacing: '-0.022em' }}>Cell</span>
          <span style={{ fontWeight: 700, color: C.cream100, letterSpacing: '-0.042em' }}>Reveal</span>
        </div>
      </div>

      {/* Tagline */}
      <div style={{
        position: 'absolute', left: 0, right: 0,
        top: ARC_CY + ARC_OUTER_R + 210,
        textAlign: 'center',
        opacity: tagT,
        transform: `translateY(${(1 - tagT) * 8}px)`,
      }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 15, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(94,234,212,0.6)' }}>
          CONTEXT-MATCHED SIGNATURES&nbsp;&nbsp;·&nbsp;&nbsp;EXPERT DECONVOLUTION ENGINE
        </div>
      </div>
    </div>
  );
}

// ─── Scene timing ──────────────────────────────────────────────────────────────
const SCENES = {
  A: { start:  0.0, end:  4.4 },
  B: { start:  3.9, end:  7.6 },
  C: { start:  7.2, end: 11.0 },
  D: { start: 10.5, end: 18.5 },
  F: { start: 18.0, end: 27.0 },
};
// Global time when the brand wordmark first appears (used to hide the corner mark)
const BRAND_TEXT_START = 18.0 + 5.3; // = 23.3s
const TOTAL_DURATION = 27;

// ─── Main exported component ──────────────────────────────────────────────────

export function ContextMatchingAnimation({ isDark = true }: { isDark?: boolean }) {
  const debugT = typeof window !== 'undefined' ? parseFloat(new URLSearchParams(window.location.search).get('t') ?? '') : NaN;
  const [time, setTime] = useState(isNaN(debugT) ? 0 : debugT);
  const rafRef        = useRef<number | null>(null);
  const lastTsRef     = useRef<number | null>(null);
  const containerRef  = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const computeScale = () => {
    if (containerRef.current) {
      setScale(containerRef.current.getBoundingClientRect().width / STAGE_W);
    }
  };

  // Scale the 1920×1080 stage to fit the container width
  useEffect(() => {
    computeScale();
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => computeScale());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Only animate when visible in viewport (saves CPU)
  useEffect(() => {
    if (!containerRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.05 }
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!isNaN(debugT)) return; // frozen at ?t= param
    if (!isVisible) { lastTsRef.current = null; return; }
    const step = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      setTime(t => (t + dt) % TOTAL_DURATION);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); lastTsRef.current = null; };
  }, [isVisible, debugT]);

  const s = scale ?? 0;
  const stageHeight = Math.round(STAGE_H * s);
  const progress = time / TOTAL_DURATION;

  return (
    <section aria-label="Context matching animation — why signatures matter">

      {/* Header zone */}
      <div className={`border-t transition-colors duration-300 ${
        isDark
          ? 'bg-navy-950 border-teal-900/25'
          : 'bg-white border-slate-100'
      }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
          <p className={`font-mono text-[9px] uppercase tracking-[0.35em] mb-4 transition-colors duration-300 ${
            isDark ? 'text-teal-400/40' : 'text-slate-400'
          }`}>
            Why signatures matter
          </p>
          <h2
            className="text-3xl md:text-[2.6rem] font-bold leading-[1.1] tracking-tight"
            style={{ textWrap: 'balance' } as CSSProperties}
          >
            <span className={`block transition-colors duration-300 ${isDark ? 'text-teal-300' : 'text-teal-600'}`}>
              The right signature
            </span>
            <span className={`block transition-colors duration-300 ${isDark ? 'text-white/90' : 'text-slate-900'}`}>
              resolves what a generic reference misses.
            </span>
          </h2>
          <p className={`mt-4 text-base leading-relaxed max-w-2xl transition-colors duration-300 ${
            isDark ? 'text-teal-100/55' : 'text-slate-600'
          }`}>
            A blood signature applied to tumor tissue will miss the cell types that matter most. CellReveal automatically matches each sample to the right reference — by tissue, disease, and cohort.
          </p>
        </div>
      </div>

      {/* Gradient bridge (light mode only) */}
      {!isDark && (
        <div style={{ height: 56, background: 'linear-gradient(to bottom, #ffffff, rgb(8 12 22))' }} />
      )}

      {/* Animation stage — always dark navy */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: scale !== null ? stageHeight : 0,
          position: 'relative',
          overflow: 'hidden',
          background: C.navy950,
          transition: 'height 150ms ease',
        }}
      >
        {scale !== null && (
          <div
            style={{
              position: 'absolute', top: 0, left: 0,
              width: STAGE_W, height: STAGE_H,
              transform: `scale(${s})`,
              transformOrigin: 'top left',
              overflow: 'hidden',
            }}
          >
            <TimelineContext.Provider value={{ time, duration: TOTAL_DURATION }}>
              <StageBackground />
              <CornerMark brandSceneStart={BRAND_TEXT_START} />
              <Sprite start={SCENES.A.start} end={SCENES.A.end}><SceneData /></Sprite>
              <Sprite start={SCENES.B.start} end={SCENES.B.end}><SceneQuestion /></Sprite>
              <Sprite start={SCENES.C.start} end={SCENES.C.end}><SceneWrong /></Sprite>
              <Sprite start={SCENES.D.start} end={SCENES.D.end}><SceneRight /></Sprite>
              <Sprite start={SCENES.F.start} end={SCENES.F.end}><SceneAlgorithm /></Sprite>
            </TimelineContext.Provider>
          </div>
        )}
      </div>

      {/* Progress bar — teal hairline */}
      <div style={{ position: 'relative', height: 2, background: 'rgba(20,184,166,0.12)' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${progress * 100}%`,
          background: `linear-gradient(to right, ${C.teal700}, ${C.teal400})`,
          boxShadow: `0 0 8px 0 ${C.teal500}`,
          transition: 'width 80ms linear',
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: `${progress * 100}%`,
          width: 6, height: 6, borderRadius: '50%',
          background: C.teal300,
          transform: 'translate(-50%, -50%)',
          boxShadow: `0 0 8px 2px ${C.teal400}`,
        }} />
      </div>

    </section>
  );
}
